/**
 * When a person last left a workspace's Overview, in this browser (spec 155
 * US1). A convenience: if storage is unavailable (a private window, cleared
 * or blocked site data) every read is a first visit and every write is
 * skipped, without an error.
 */

export interface Visit {
  /** When the Overview was last left; `null` on a first visit. */
  leftAt: string | null;
  /** When "Since you were here" was last dismissed. */
  dismissedAt: string | null;
}

const NONE: Visit = { leftAt: null, dismissedAt: null };

function keyOf(userId: string, workspaceId: string): string {
  return `overview.visit.${userId}.${workspaceId}`;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readVisit(userId: string, workspaceId: string): Visit {
  try {
    const raw = storage()?.getItem(keyOf(userId, workspaceId));
    if (!raw) return NONE;
    const parsed = JSON.parse(raw) as Partial<Visit> | null;
    return {
      leftAt: typeof parsed?.leftAt === 'string' ? parsed.leftAt : null,
      dismissedAt:
        typeof parsed?.dismissedAt === 'string' ? parsed.dismissedAt : null,
    };
  } catch {
    return NONE;
  }
}

function write(
  userId: string,
  workspaceId: string,
  change: Partial<Visit>,
): void {
  try {
    const next = { ...readVisit(userId, workspaceId), ...change };
    storage()?.setItem(keyOf(userId, workspaceId), JSON.stringify(next));
  } catch {
    // Storage refused: the strip simply has nothing to compare with next time.
  }
}

export function writeLeft(userId: string, workspaceId: string, at: Date): void {
  write(userId, workspaceId, { leftAt: at.toISOString() });
}

export function writeDismissed(
  userId: string,
  workspaceId: string,
  at: Date,
): void {
  write(userId, workspaceId, { dismissedAt: at.toISOString() });
}
